import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent
} from '@mui/joy';
import { CheckCircle, CurrencyDollar as DollarSign } from '@phosphor-icons/react';
import type { Job, BoardStatus } from '../types';
import { JobCard } from './JobCard';

interface KanbanColumnProps {
  status: BoardStatus;
  jobs: Job[];
  onAddJob?: (statusId: number) => void;
  onEditJob?: (job: Job) => void;
  onDeleteJob?: (jobId: number) => void;
  onUncompleteJob?: (jobId: number) => void;
  /** Board accent color (hex); used as a tint in the column header and body */
  accentColor?: string;
  cardBackgroundColor?: string;
  isDropTarget?: boolean;
}

// rgba() tint of a hex color, used to derive header/body shades from the board color
const tint = (hexColor: string, opacity: number) => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return undefined;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  status,
  jobs,
  onEditJob,
  onDeleteJob,
  onUncompleteJob,
  accentColor,
  cardBackgroundColor,
  isDropTarget = false,
}) => {
  const { setNodeRef } = useDroppable({
    id: `column-${status.id}`,
  });

  const activeJobs = jobs.filter(j => !j.is_completed);
  const completedJobs = jobs.filter(j => j.is_completed);
  const totalValue = jobs.reduce((sum, job) => {
    const value = parseFloat(String(job.value || 0));
    return sum + (isNaN(value) ? 0 : value);
  }, 0);

  const headerTint = accentColor ? tint(accentColor, 0.16) : undefined;
  const headerBorder = accentColor ? tint(accentColor, 0.4) : undefined;
  const bodyTint = accentColor ? tint(accentColor, 0.07) : undefined;
  const bodyBorder = accentColor ? tint(accentColor, 0.3) : undefined;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        borderRadius: 'lg',
        border: isDropTarget ? '3px dashed' : '2px solid transparent',
        borderColor: isDropTarget ? 'primary.500' : 'transparent',
        boxShadow: isDropTarget ? 'inset 0 0 0 3px var(--joy-palette-primary-300, #93c5fd)' : 'none',
        bgcolor: isDropTarget ? 'primary.softBg' : 'transparent',
        transition: 'all 0.15s ease',
        position: 'relative',
        p: isDropTarget ? '2px' : 0,
      }}
    >
      {/* Drop hint overlay */}
      {isDropTarget && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 10,
        }}>
          <Typography
            level="title-md"
            sx={{
              color: 'var(--brand-text, #7013f5)',
              fontWeight: 'bold',
              bgcolor: 'background.surface',
              px: 2,
              py: 1,
              borderRadius: 'sm',
              border: '2px dashed',
              borderColor: 'primary.400',
              opacity: 0.9,
            }}
          >
            Drop here
          </Typography>
        </Box>
      )}

      {/* Column Header */}
      <Card
        variant="outlined"
        sx={{
          mb: 1,
          flexShrink: 0,
          borderRadius: 'lg',
          overflow: 'hidden',
          ...(accentColor && {
            borderColor: headerBorder,
            borderTop: '3px solid',
            borderTopColor: accentColor,
            backgroundImage: `linear-gradient(${headerTint}, ${headerTint})`,
          }),
        }}
      >
        <CardContent sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="title-sm" fontWeight="lg">
                {status.name}
              </Typography>
              <Chip size="sm" variant="soft" color="neutral">
                {jobs.length}
              </Chip>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DollarSign size={12} />
            <Typography level="body-xs" textColor="text.secondary">
              Total: {formatCurrency(totalValue)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Card List */}
      <Box
        className="kanban-column"
        sx={{
          flex: 1,
          bgcolor: 'background.surface',
          borderRadius: 'lg',
          p: 1,
          border: '1px solid',
          borderColor: bodyBorder || 'neutral.200',
          ...(bodyTint && {
            backgroundImage: `linear-gradient(${bodyTint}, ${bodyTint})`,
          }),
          transition: 'background-color 0.15s ease',
          overflow: 'auto',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <SortableContext items={jobs.map(job => job.id)} strategy={verticalListSortingStrategy}>
          {activeJobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onEdit={onEditJob}
              onDelete={onDeleteJob}
              onUncomplete={onUncompleteJob}
              backgroundColor={cardBackgroundColor}
            />
          ))}

          {completedJobs.length > 0 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1,
                  py: 0.5,
                  mt: activeJobs.length > 0 ? 0.5 : 0,
                  bgcolor: 'success.solidBg',
                  borderRadius: 'sm',
                }}
              >
                <CheckCircle size={12} style={{ color: 'white' }} />
                <Typography level="body-xs" sx={{ color: 'common.white', fontWeight: 600 }}>
                  Completed ({completedJobs.length})
                </Typography>
              </Box>
              {completedJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onEdit={onEditJob}
                  onDelete={onDeleteJob}
                  onUncomplete={onUncompleteJob}
                  backgroundColor={cardBackgroundColor}
                />
              ))}
            </>
          )}
        </SortableContext>

        {jobs.length === 0 && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            gap: 2,
            flex: 1,
          }}>
            <Typography level="body-sm" textColor="text.secondary">
              No jobs in this column
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
